import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { LocalService } from './local.service';
import { Router } from '@angular/router';
import { ApiService } from './api.service';
import { LoginParam, RegisterParam } from '../../shared/model/auth.model';
import { Observable, finalize, firstValueFrom, map, shareReplay } from 'rxjs';
import { ConfigService } from './config.service';
import { ApiModel } from '../../shared/model/api.model';
import { IdbService } from './idb.service';
import { LanguageService } from './language.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  private platformId = inject(PLATFORM_ID);

  // The one refresh request in flight (shared by every caller, errors included).
  private refresh$: Observable<string> | null = null;

  private service = 'recruitment-http/recruitment';
  private module = 'auth';

  constructor(
    private localService: LocalService,
    private apiService: ApiService,
    private router: Router,
    private configService: ConfigService,
    private idbService: IdbService,
    private languageService: LanguageService,
  ) { }

  hasPermissions(permissions: string[] | string, operator: 'and' | 'or' = 'and'): boolean {
    if (typeof permissions === 'string') {
      permissions = [permissions];
    }

    const userPermissions = this.configService.getPermissions();
    switch (operator) {
      case 'or':
        return permissions.some(permission => userPermissions[permission]);
      case 'and':
        return permissions.every(permission => userPermissions[permission]);
      default:
        return false;
    }
  }

  async logout(): Promise<boolean> {
    return firstValueFrom(this.apiService.post(this.service, `${this.module}/v1/logout`, {}, {
      withCredentials: true,
    })).then(() => {
      this.localService.clearData();
      this.idbService.clearAllFormData();
      this.router.navigate([`/${ this.languageService.getLang() }`]);

      if (isPlatformBrowser(this.platformId)) {
        window.location.reload();
      }

      return true;
    }).catch(() => {
      return false;
    });
  }

  refreshToken(): Observable<string> {
    if (!this.refresh$) {
      this.refresh$ = this.getRefreshTokenHttpRequest().pipe(
        map((res: ApiModel<any>) => {
          const newToken = res.data.token;
          this.configService.setToken(newToken);
          this.configService.setUser(res.data.user);

          return newToken as string;
        }),
        finalize(() => {
          this.refresh$ = null;
        }),
        shareReplay({ bufferSize: 1, refCount: false }),
      );
    }

    return this.refresh$;
  }

  /** Seconds until the access token expires (negative when already expired); null if unknown. */
  getTokenExpiresIn(token = this.configService.getToken()): number | null {
    if (!token || !isPlatformBrowser(this.platformId)) {
      return null;
    }

    try {
      const payload = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
      const exp = JSON.parse(atob(payload)).exp;

      return typeof exp === 'number' ? exp - Date.now() / 1000 : null;
    } catch {
      return null;
    }
  }

  getRefreshTokenHttpRequest() {
    return this.apiService.post(this.service, `${this.module}/v1/refresh-token`, {}, {
      withCredentials: true,
    });
  }

  captcha() {
    return this.apiService.get('auth-http', 'v2/captcha');
  }

  login(data: LoginParam) {
    return this.apiService.post(this.service, `${this.module}/v1/login`, data, {
      withCredentials: true,
    });
  }

  loginSSO(data: { token: string }) {
    return this.apiService.post(this.service, `${this.module}/v1/sso/login`, data, {
      withCredentials: true,
    });
  }

  register(data: RegisterParam) {
    return this.apiService.post(this.service, `${this.module}/v1/register`, data, {
      withCredentials: true,
    });
  }

  requestOtp(userUuid: string, lang = this.localService.getData('lang') || 'en') {
    return this.apiService.get(this.service, `${this.module}/v1/otp/request/${userUuid}`, { lang });
  }

  verifyEmail(data: { otp: string, user_uuid: string }) {
    return this.apiService.post(this.service, `${this.module}/v1/user/email/verify`, data);
  }

  resetPasswordRequest(data: { email: string }) {
    return this.apiService.post(this.service, `${this.module}/v1/reset-password/request`, data);
  }

  resetPasswordTokenCheck(token: string) {
    return this.apiService.get(this.service, `${this.module}/v1/reset-password/check/${token}`);
  }

  resetPassword(data: { password: string, confirm_password: string, reset_token: string }) {
    return this.apiService.post(this.service, `${this.module}/v1/reset-password`, data);
  }

  googleLogin(data: { credential: string }) {
    return this.apiService.post(this.service, `${this.module}/v1/google/login`, data, {
      withCredentials: true,
    });
  }
}
